/**
 * Plan, add-on and coupon pricing for member checkout.
 *
 * MVP: values are hard-coded here to mirror the Stripe products/prices. When the
 * Stripe catalog is wired up, replace `getCheckoutPlan` / `findCoupon` with API
 * lookups — the rest of the flow consumes the same shapes.
 */

export interface CheckoutPlan {
  id: string;
  name: string;
  /** Recurring subscription price, in cents. */
  monthlyCents: number;
  /** One-time account setup / activation fee, in cents. */
  setupFeeCents: number;
  documentsNote: string;
}

export interface AcceleratorCohort {
  id: string;
  label: string;
}

export interface CouponDefinition {
  code: string;
  label: string;
  kind: 'amount' | 'percent';
  /** Cents when `kind` is `amount`, whole percent when `kind` is `percent`. */
  value: number;
  expired?: boolean;
}

export interface OrderLine {
  id: string;
  label: string;
  cents: number;
  /** True for the subscription line — charged now and again every month. */
  recurring?: boolean;
}

export interface OrderSummary {
  lines: OrderLine[];
  monthlyCents: number;
  dueNowCents: number;
}

const PLANS: Record<string, CheckoutPlan> = {
  team: {
    id: 'team',
    name: 'Team Plan',
    monthlyCents: 9900,
    setupFeeCents: 9900,
    documentsNote:
      'You will be asked to provide these required company documents during your account setup: EIN Letter and LLC or SCORP',
  },
  solo: {
    id: 'solo',
    name: 'Solo Plan',
    monthlyCents: 4900,
    setupFeeCents: 9900,
    documentsNote:
      'You will be asked to provide these required company documents during your account setup: EIN Letter and LLC or SCORP',
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise Plan',
    monthlyCents: 19900,
    setupFeeCents: 9900,
    documentsNote:
      'You will be asked to provide these required company documents during your account setup: EIN Letter and LLC or SCORP',
  },
};

export const ACCELERATOR_CENTS = 35000;

export const ACCELERATOR_COHORTS: AcceleratorCohort[] = [
  { id: 'sep-2026', label: 'September 8–12, 2026' },
  { id: 'oct-2026', label: 'October 13–17, 2026' },
  { id: 'nov-2026', label: 'November 10–14, 2026' },
];

export const ACCELERATOR_FORMATS = [
  { id: 'in_person', label: 'In person — Atlanta, GA' },
  { id: 'virtual', label: 'Virtual — live online' },
] as const;

/** Coupons recognised for MVP. Unknown codes are reported as invalid. */
const COUPONS: CouponDefinition[] = [
  { code: 'WELCOME25', label: '$25.00 off your first payment', kind: 'amount', value: 2500 },
  { code: 'ADVISOR10', label: '10% off your first payment', kind: 'percent', value: 10 },
  { code: 'SPRING2025', label: 'Spring 2025 promotion', kind: 'amount', value: 5000, expired: true },
];

export const DEFAULT_PLAN_ID = 'team';

export const getCheckoutPlan = (planId?: string | null): CheckoutPlan =>
  PLANS[(planId ?? '').toLowerCase()] ?? PLANS[DEFAULT_PLAN_ID];

export const findCoupon = (code: string): CouponDefinition | undefined =>
  COUPONS.find((c) => c.code === code.trim().toUpperCase());

export const formatCents = (cents: number): string =>
  `${cents < 0 ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)} USD`;

interface OrderInput {
  plan: CheckoutPlan;
  acceleratorAdded: boolean;
  couponCode?: string;
}

/**
 * Builds the itemised order. Adding the Accelerator waives the one-time setup
 * fee (its Stripe coupon), shown as an explicit credit line so the member can
 * see why the total changed.
 */
export const buildOrderSummary = ({
  plan,
  acceleratorAdded,
  couponCode,
}: OrderInput): OrderSummary => {
  const lines: OrderLine[] = [
    { id: 'plan', label: 'Subtotal (monthly)', cents: plan.monthlyCents, recurring: true },
    { id: 'setup', label: 'One-Time Account Setup Fee', cents: plan.setupFeeCents },
  ];

  if (acceleratorAdded) {
    lines.push({ id: 'accelerator', label: 'Accelerator Course', cents: ACCELERATOR_CENTS });
    lines.push({
      id: 'accelerator-waiver',
      label: 'Account setup fee waived (Accelerator)',
      cents: -plan.setupFeeCents,
    });
  }

  const coupon = couponCode ? findCoupon(couponCode) : undefined;
  const preCouponDueNow = lines.reduce((total, line) => total + line.cents, 0);

  if (coupon && !coupon.expired) {
    const discount =
      coupon.kind === 'amount'
        ? Math.min(coupon.value, preCouponDueNow)
        : Math.round((preCouponDueNow * coupon.value) / 100);
    lines.push({ id: 'coupon', label: `Coupon ${coupon.code}`, cents: -discount });
  }

  const dueNowCents = lines.reduce((total, line) => total + line.cents, 0);

  return { lines, monthlyCents: plan.monthlyCents, dueNowCents: Math.max(0, dueNowCents) };
};
